import { cn } from '@/utils/cn';
import { Loader2, ArrowRight, ArrowLeft, ArrowLeftRight } from 'lucide-react';
import type { SyncEntityType, SyncDirection } from '@/types';

interface SyncButtonProps {
  entityType: SyncEntityType;
  direction: SyncDirection;
  onSync: (entityType: SyncEntityType, direction: SyncDirection, fullSync?: boolean) => Promise<unknown>;
  isLoading?: boolean;
  disabled?: boolean;
  /** If true, this sync will re-sync all items including already mapped ones */
  fullSync?: boolean;
}

const directionConfig = {
  to_xero: {
    label: 'Sync to Xero',
    icon: ArrowRight,
    tooltip: 'Sync data from HighLevel to Xero',
    bgColor: 'bg-[#13B5EA]',
    hoverBgColor: 'hover:bg-[#0E8FC4]',
  },
  from_xero: {
    label: 'Sync from Xero',
    icon: ArrowLeft,
    tooltip: 'Sync data from Xero to HighLevel',
    bgColor: 'bg-[#7C3AED]',
    hoverBgColor: 'hover:bg-[#6D28D9]',
  },
  bidirectional: {
    label: 'Bidirectional Sync',
    icon: ArrowLeftRight,
    tooltip: 'Sync data in both directions',
    bgColor: 'bg-gray-700',
    hoverBgColor: 'hover:bg-gray-800',
  },
};

export function SyncButton({
  entityType,
  direction,
  onSync,
  isLoading = false,
  disabled = false,
  fullSync = false,
}: SyncButtonProps) {
  const config = directionConfig[direction];
  const Icon = config.icon;

  const handleClick = async () => {
    if (!disabled && !isLoading) {
      await onSync(entityType, direction, fullSync);
    }
  };

  // Update tooltip to indicate full vs smart sync
  const tooltip = fullSync
    ? `${config.tooltip} (Full - all items)`
    : `${config.tooltip} (Smart - unmapped only)`;

  return (
    <button
      onClick={handleClick}
      disabled={disabled || isLoading}
      title={tooltip}
      className={cn(
        'flex w-full items-center justify-center space-x-2 rounded-md px-4 py-2.5 text-sm font-medium transition-colors',
        config.bgColor,
        config.hoverBgColor,
        'text-white',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500'
      )}
    >
      {isLoading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Syncing...</span>
        </>
      ) : (
        <>
          <Icon className="h-4 w-4" />
          <span>{config.label}</span>
        </>
      )}
    </button>
  );
}
